// src/components/DonateModal.jsx
import { useState } from 'react';
import { useLang } from '../context/LanguageContext';
import { donateConfig } from '../data/translations';

const donateT = {
  en: { title: 'Make a Donation', subtitle: 'Your generosity helps empower women and children.', chooseMethod: 'Choose Payment Method', selectAmount: 'Select Amount (FCFA)', custom: 'Custom amount', placeholder: 'Enter amount', name: 'Your Name', namePh: 'Full name', phone: 'Your Phone Number', phonePh: '6XXXXXXXX', message: 'Message (optional)', messagePh: 'A few words of support...', recipient: 'Recipient Number', submit: 'Donate Now', processing: 'Processing...', successTitle: 'Thank You!', successMsg: 'Your donation has been submitted. You will receive a confirmation on your phone.', close: 'Close', back: 'Make another donation', apiNote: 'Payment system is being set up. Please contact us via WhatsApp to make your donation.' },
  fr: { title: 'Faire un Don', subtitle: 'Votre générosité aide à autonomiser les femmes et les enfants.', chooseMethod: 'Choisir le mode de paiement', selectAmount: 'Sélectionner le montant (FCFA)', custom: 'Montant personnalisé', placeholder: 'Entrer le montant', name: 'Votre Nom', namePh: 'Nom complet', phone: 'Votre Numéro de Téléphone', phonePh: '6XXXXXXXX', message: 'Message (facultatif)', messagePh: 'Quelques mots de soutien...', recipient: 'Numéro du bénéficiaire', submit: 'Donner Maintenant', processing: 'Traitement en cours...', successTitle: 'Merci !', successMsg: 'Votre don a été soumis. Vous recevrez une confirmation sur votre téléphone.', close: 'Fermer', back: 'Faire un autre don', apiNote: 'Le système de paiement est en cours de configuration. Contactez-nous via WhatsApp pour faire votre don.' },
  es: { title: 'Hacer una Donación', subtitle: 'Tu generosidad ayuda a empoderar a mujeres y niños.', chooseMethod: 'Elegir método de pago', selectAmount: 'Seleccionar monto (FCFA)', custom: 'Monto personalizado', placeholder: 'Ingresar monto', name: 'Tu Nombre', namePh: 'Nombre completo', phone: 'Tu Número de Teléfono', phonePh: '6XXXXXXXX', message: 'Mensaje (opcional)', messagePh: 'Unas palabras de apoyo...', recipient: 'Número del destinatario', submit: 'Donar Ahora', processing: 'Procesando...', successTitle: '¡Gracias!', successMsg: 'Tu donación ha sido enviada. Recibirás una confirmación en tu teléfono.', close: 'Cerrar', back: 'Hacer otra donación', apiNote: 'El sistema de pago está siendo configurado. Contáctanos por WhatsApp.' },
};

export default function DonateModal({ isOpen, onClose }) {
  const { lang } = useLang();
  const dt = donateT[lang] || donateT.fr;
  const [provider, setProvider] = useState(null);
  const [amount, setAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [form, setForm] = useState({ name: '', phone: '', message: '' });
  const [step, setStep] = useState('form'); // 'form' | 'success'
  const [loading, setLoading] = useState(false);

  const reset = () => { setProvider(null); setAmount(null); setCustomAmount(''); setForm({ name: '', phone: '', message: '' }); setStep('form'); setLoading(false); };
  const handleClose = () => { reset(); onClose(); };

  const finalAmount = amount || parseInt(customAmount) || 0;
  const recipientNumber = provider === 'orange' ? donateConfig.ORANGE_RECIPIENT : donateConfig.MTN_RECIPIENT;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await new Promise(r => setTimeout(r, 1500));
    setLoading(false);
    setStep('success');
  };

  if (!isOpen) return null;

  return (
    <div className={`donate-modal-overlay${isOpen ? ' active' : ''}`} onClick={e => e.target === e.currentTarget && handleClose()}>
      <div className="donate-modal-card">
        <div className="donate-header">
          <button className="donate-close-btn" onClick={handleClose} aria-label="Close">×</button>
          <div style={{ width: 70, height: 70, margin: '0 auto 1rem', background: 'linear-gradient(135deg,#e74c3c,#c0392b)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="fas fa-hand-holding-heart" style={{ fontSize: '2rem', color: 'white' }}></i>
          </div>
          <h2>{dt.title}</h2>
          <p style={{ color: 'rgba(26,26,26,0.6)', fontSize: '0.95rem' }}>{dt.subtitle}</p>
        </div>

        <div className="donate-body">
          {step === 'success' ? (
            <div className="donate-success">
              <i className="fas fa-check-circle"></i>
              <h3 style={{ fontSize: '1.5rem', margin: '1rem 0 0.5rem' }}>{dt.successTitle}</h3>
              <p style={{ color: 'rgba(26,26,26,0.7)', marginBottom: '2rem' }}>{dt.successMsg}</p>
              <button className="btn-primary" onClick={reset} style={{ marginRight: '1rem' }}>{dt.back}</button>
              <button className="btn-outline" onClick={handleClose}>{dt.close}</button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>{dt.chooseMethod}</label>
                <div className="payment-methods">
                  {[{ id: 'orange', label: 'Orange Money', icon: '🟠', color: '#FF6900' },
                    { id: 'mtn', label: 'MTN Mobile Money', icon: '🟡', color: '#FFCC00' }].map(m => (
                    <div key={m.id} className={`payment-method${provider === m.id ? ' selected' : ''}`} onClick={() => setProvider(m.id)}>
                      <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>{m.icon}</div>
                      <div style={{ fontWeight: 700, fontSize: '0.875rem', color: m.color }}>{m.label}</div>
                      {provider === m.id && <div style={{ color: 'var(--primary)', marginTop: '0.25rem' }}><i className="fas fa-check-circle"></i></div>}
                    </div>
                  ))}
                </div>
              </div>

              {provider && (
                <div className="form-group" style={{ background: 'var(--bg-card)', padding: '0.75rem', borderRadius: 'var(--border-radius)', fontSize: '0.875rem', marginBottom: '1rem' }}>
                  <i className="fas fa-phone" style={{ color: 'var(--primary)', marginRight: 8 }}></i>
                  <strong>{dt.recipient} :</strong> {recipientNumber}
                </div>
              )}

              <div className="form-group">
                <label>{dt.selectAmount}</label>
                <div className="amount-grid">
                  {donateConfig.PRESET_AMOUNTS.map(a => (
                    <button key={a} type="button" className={`amount-btn${amount === a ? ' selected' : ''}`} onClick={() => { setAmount(a); setCustomAmount(''); }}>
                      {a.toLocaleString()} FCFA
                    </button>
                  ))}
                </div>
                <input type="number" className="form-control" placeholder={dt.placeholder} style={{ marginTop: '0.75rem' }} value={customAmount} min="100" onChange={e => { setCustomAmount(e.target.value); setAmount(null); }} />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{dt.name}</label>
                  <input type="text" className="form-control" placeholder={dt.namePh} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
                </div>
                <div className="form-group">
                  <label>{dt.phone}</label>
                  <input type="tel" className="form-control" placeholder={dt.phonePh} value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} required />
                </div>
              </div>

              <div className="form-group">
                <label>{dt.message}</label>
                <textarea className="form-control" placeholder={dt.messagePh} rows={3} value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} />
              </div>

              <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 'var(--border-radius)', padding: '0.75rem', marginBottom: '1rem', fontSize: '0.85rem', color: '#856404' }}>
                <i className="fas fa-info-circle" style={{ marginRight: 8 }}></i>
                {dt.apiNote}
              </div>

              <a href={`https://wa.me/${donateConfig.WHATSAPP}?text=Je%20souhaite%20faire%20un%20don%20de%20${finalAmount}%20FCFA%20via%20${provider === 'orange' ? 'Orange%20Money' : 'MTN%20MoMo'}.%20Nom%3A%20${encodeURIComponent(form.name)}`}
                target="_blank" rel="noopener noreferrer" className="btn-primary" style={{ width: '100%', justifyContent: 'center', textDecoration: 'none', marginBottom: '0.75rem' }}>
                <i className="fab fa-whatsapp"></i>
                Donner via WhatsApp
              </a>

              <button type="submit" className="btn-outline" style={{ width: '100%', justifyContent: 'center' }} disabled={loading}>
                {loading ? <><i className="fas fa-spinner fa-spin"></i> {dt.processing}</> : <><i className="fas fa-heart"></i> {dt.submit}</>}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
